import clsx from 'clsx';
import React from 'react';
import type { IconName, IconSize } from '../../icons/types';
import useFormFieldAria from '../../hooks/useFormFieldAria';
import Icon from '../Icon/Icon';

type CircularProgressVariant = 'ring' | 'donut';
type CircularProgressSize = 'sm' | 'md' | 'lg';

// Bounded sizes: each one fixes the diameter, ring stroke, center icon and the
// percentage font so consumers can't produce overlapping/incompatible combos.
// Need another size? Add it here, not per consumer.
const SIZES: Record<CircularProgressSize, { diameter: number; strokeWidth: number; iconSize: IconSize }> = {
  sm: { diameter: 36, strokeWidth: 3, iconSize: 'sm' },
  md: { diameter: 40, strokeWidth: 3, iconSize: 'sm' },
  lg: { diameter: 50, strokeWidth: 4, iconSize: 'md' },
};

type CircularProgressProps = {
  backgroundColor?: string;
  completedIcon?: IconName;
  hasCenterContent?: boolean;
  id?: string;
  label: string;
  progress?: number;
  progressColor?: string;
  size?: CircularProgressSize;
  variant?: CircularProgressVariant;
};

/**
 * CircularProgress — circular progress indicator drawn as a hand-rolled SVG ring.
 *
 * The `ring` variant is a thin stroke with a rounded cap. It owns its center
 * content: the `completedIcon` at 100%, the percentage while in progress, and
 * nothing at 0% — so consumers don't reimplement that logic. The `donut`
 * variant is the same ring drawn with a thick, butt-capped stroke so it reads
 * as a filled pie — it consolidates the former `ProgressDonut`.
 *
 * `size` is a bounded token (`sm` | `md` | `lg`); each one fixes diameter and
 * stroke so consumers pick a size instead of tuning raw pixels.
 */
function CircularProgress({
  backgroundColor,
  completedIcon = 'check',
  hasCenterContent = true,
  id,
  label,
  progress = 0,
  progressColor,
  size = 'md',
  variant = 'ring',
}: Readonly<CircularProgressProps>): JSX.Element {
  const { ids } = useFormFieldAria({ id });

  const { diameter, strokeWidth, iconSize } = SIZES[size] ?? SIZES.md;
  const isDonut = variant === 'donut';
  const clampedProgress = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 100) : 0;
  const center = diameter / 2;

  // Ring: a thin stroked arc over a full-circle track.
  // Donut: a solid outer ring (background disc) + a white inner disc + a thick
  // pie that grows inside the disc. Both share the arc below, differing only in
  // radius/stroke.
  const donutRingWidth = diameter * 0.12;
  const donutInnerRadius = center - donutRingWidth;
  const arcRadius = isDonut ? donutInnerRadius / 2 : (diameter - strokeWidth) / 2;
  const arcStroke = isDonut ? donutInnerRadius : strokeWidth;

  const circumference = arcRadius * 2 * Math.PI;
  const offset = circumference - (clampedProgress / 100) * circumference;

  // The ring owns its center: nothing at 0%, the percentage while in progress,
  // and the completed icon at 100%. The donut never shows center content.
  const isCompleted = clampedProgress >= 100;
  const shouldShowCenterContent = !isDonut && hasCenterContent && clampedProgress > 0;

  // Colours default from the SCSS custom properties; a caller only overrides
  // them by feeding the prop back into the same custom property. No token
  // strings live in this component. `backgroundColor` maps to the track (ring)
  // or the outer ring (donut); `progressColor` maps to the arc/pie.
  const style = {
    width: diameter,
    height: diameter,
    ...(progressColor ? { '--circular-progress-fill': progressColor } : {}),
    ...(backgroundColor ? { '--circular-progress-background': backgroundColor } : {}),
  } as React.CSSProperties;

  return (
    <div
      aria-labelledby={ids.labelId}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={clampedProgress}
      className={clsx('circular-progress', `circular-progress--${variant}`, `circular-progress--size-${size}`)}
      role="progressbar"
      style={style}
    >
      <span className="sr-only" id={ids.labelId}>
        {label}
      </span>
      <svg className="circular-progress__svg" height={diameter} viewBox={`0 0 ${diameter} ${diameter}`} width={diameter}>
        {isDonut ? (
          <>
            <circle className="circular-progress__background" cx={center} cy={center} r={center} />
            <circle className="circular-progress__donut-center" cx={center} cy={center} r={donutInnerRadius} />
          </>
        ) : (
          <circle className="circular-progress__track" cx={center} cy={center} fill="none" r={arcRadius} strokeWidth={arcStroke} />
        )}
        <circle
          className="circular-progress__fill"
          cx={center}
          cy={center}
          fill="none"
          r={arcRadius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap={isDonut ? 'butt' : 'round'}
          strokeWidth={arcStroke}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      {shouldShowCenterContent && (
        <div aria-hidden="true" className="circular-progress__content">
          {isCompleted ? <Icon color="primary" name={completedIcon} size={iconSize} /> : <span className="circular-progress__value">{clampedProgress}%</span>}
        </div>
      )}
    </div>
  );
}

export default CircularProgress;
export type { CircularProgressProps, CircularProgressSize, CircularProgressVariant };
